/**
 * Bridge Login Page ViewModel
 *
 * Handles authentication in the device browser when triggered from a webview.
 * After successful Web3Auth login, completes the bridge with tokens so the
 * webview can retrieve them via polling.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import web3authGateway, { type Web3AuthUserInfo } from '../services/web3auth.gateway';
import { authBridgeApi } from '../services/auth-bridge.api';
import { useAuthStore, type AuthUser } from '../stores/useAuthStore';
import { useAuthNavigation } from '../hooks/useAuthNavigation';
import { startTokenRefreshScheduler } from '@/lib/token-refresh';
import { isCredentialRejection, clearRejectedSession } from '../services/session-recovery';

const SOKA_API_URL = import.meta.env.VITE_SOKA_API_URL ?? 'https://dev.soka.club/api';

type BridgeStatus = 'ready' | 'authenticating' | 'completing' | 'success' | 'error';

interface BridgeAuthData {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface BridgeLoginPageState {
  status: BridgeStatus;
  error: string | null;
  provider: string | null;
  bridgeId: string | null;
}

interface BridgeLoginPageActions {
  handleGoogleLogin: () => void;
  handleFacebookLogin: () => void;
  retry: () => void;
}

export function useBridgeLoginPageViewModel(): {
  state: BridgeLoginPageState;
  actions: BridgeLoginPageActions;
  onMount: () => void;
} {
  const [searchParams] = useSearchParams();
  const bridgeId = searchParams.get('bridgeId');
  const provider = searchParams.get('provider');

  const { setTokens, setUser } = useAuthStore();
  const { navigateToHome } = useAuthNavigation();

  const [status, setStatus] = useState<BridgeStatus>('ready');
  const [error, setError] = useState<string | null>(null);

  // Guard against double completeAuth calls (popup mode fires both callback and promise)
  const hasCompletedRef = useRef(false);

  // Store auth data after bridge completion so "stay in browser" can use it
  const authDataRef = useRef<BridgeAuthData | null>(null);

  // Validate params
  useEffect(() => {
    if (!bridgeId) {
      setError('Missing bridge ID. Please try again from the app.');
      setStatus('error');
    }
  }, [bridgeId]);

  const completeAuth = useCallback(
    async (userInfo: Web3AuthUserInfo) => {
      if (!bridgeId || hasCompletedRef.current) return;
      hasCompletedRef.current = true;

      setStatus('completing');

      try {
        if (!userInfo.idToken) {
          throw new Error('No idToken received from Web3Auth');
        }

        // Story 33-06: the gateway's mapper returns no typeOfLogin for a connection
        // it does not recognise, rather than substituting a literal. Registering a
        // placeholder would write it permanently into SOKA's Users.typeOfLogin, so
        // surface the mapper bug here instead — at login time, where it can be
        // investigated. This is not a credential rejection: the error carries no
        // status, so the catch below leaves the Web3Auth session intact.
        if (!userInfo.typeOfLogin) {
          throw new Error('No typeOfLogin received from Web3Auth');
        }

        // Register with SOKA API using direct fetch — NOT authApi (its axios
        // interceptor overwrites the Authorization header with the Zustand store
        // token, which is empty in this browser tab)
        const registerRes = await fetch(`${SOKA_API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.idToken}`
          },
          body: JSON.stringify({
            wallet: userInfo.wallet ?? '',
            typeOfLogin: userInfo.typeOfLogin,
            source: 'PWA'
          })
        });

        if (!registerRes.ok) {
          const errorBody = (await registerRes.json().catch(() => null)) as {
            message?: string;
          } | null;
          // Preserve the HTTP status on the thrown error so the catch block below
          // can classify a credential rejection (401/403) vs. a transient failure.
          const registerError = new Error(
            errorBody?.message ?? `Registration failed (${String(registerRes.status)})`
          ) as Error & { status: number };
          registerError.status = registerRes.status;
          throw registerError;
        }

        const authResponse = (await registerRes.json()) as {
          accessToken: string;
          refreshToken: string;
        };

        // Fetch profile using the fresh access token (direct fetch, same reason)
        let profile: {
          id?: number;
          email?: string;
          pseudo?: string;
          points?: number;
          country?: string;
        } = {};
        try {
          const profileRes = await fetch(`${SOKA_API_URL}/auth/profile`, {
            headers: { Authorization: `Bearer ${authResponse.accessToken}` }
          });
          if (profileRes.ok) {
            profile = (await profileRes.json()) as typeof profile;
          }
        } catch {
          // Profile fetch failed — continue with Web3Auth info only
        }

        // Complete the bridge with tokens + user data
        const bridgeUserData = {
          wallet: userInfo.wallet,
          name: userInfo.name,
          email: userInfo.email ?? profile.email,
          profileImage: userInfo.profileImage,
          typeOfLogin: userInfo.typeOfLogin,
          id: profile.id,
          pseudo: profile.pseudo,
          points: profile.points,
          country: profile.country
        };

        await authBridgeApi.completeBridge(bridgeId, {
          accessToken: authResponse.accessToken,
          refreshToken: authResponse.refreshToken,
          user: bridgeUserData
        });

        // Keep auth data available for "stay in browser" flow
        authDataRef.current = {
          accessToken: authResponse.accessToken,
          refreshToken: authResponse.refreshToken,
          user: bridgeUserData as AuthUser
        };

        setStatus('success');
      } catch (err) {
        hasCompletedRef.current = false; // Allow retry on error

        // Same rule as AuthContext: a credential rejection (401/403) leaves a
        // restorable Web3Auth session that would keep repeating the same failed
        // register call, so clear it. A transient failure stays retryable.
        if (isCredentialRejection(err)) {
          await clearRejectedSession();
        }

        const message = err instanceof Error ? err.message : 'Authentication failed';
        setError(message);
        setStatus('error');
        console.error('Bridge auth error:', err);
      }
    },
    [bridgeId]
  );

  const doLogin = useCallback(
    async (loginFn: () => Promise<Web3AuthUserInfo>) => {
      setError(null);
      setStatus('authenticating');
      hasCompletedRef.current = false;

      try {
        // Register callback for CONNECTED event (handles the redirect flow return)
        web3authGateway.setOnLoginSuccessCallback((userInfo) => {
          void completeAuth(userInfo);
        });

        await web3authGateway.init();
        const userInfo = await loginFn();
        // If loginFn resolves directly (popup mode), completeAuth with result
        // The ref guard prevents double execution if the callback also fires
        void completeAuth(userInfo);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        setStatus('error');
        console.error('Bridge login error:', err);
      }
    },
    [completeAuth]
  );

  const handleGoogleLogin = useCallback(() => {
    void doLogin(() => web3authGateway.connectWithGoogle());
  }, [doLogin]);

  const handleFacebookLogin = useCallback(() => {
    void doLogin(() => web3authGateway.connectWithFacebook());
  }, [doLogin]);

  const retry = useCallback(() => {
    setError(null);
    setStatus('ready');
  }, []);

  // After a successful bridge completion, automatically return to the originating
  // widget instead of asking the user to choose. The webview that opened this tab
  // has already retrieved the tokens via polling, so this tab's only remaining job
  // is to send the user back to the app.
  useEffect(() => {
    if (status !== 'success') return;

    // Primary path: close this browser tab to return to the native app/webview
    // that opened it via window.open().
    window.close();

    // Fallback: some contexts disallow programmatic close (e.g. the popup was
    // blocked and the bridge page loaded in-place). Log in here and navigate home
    // so the user is never stranded on the success screen.
    const fallbackTimer = setTimeout(() => {
      const data = authDataRef.current;
      if (!data) return;

      setTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      startTokenRefreshScheduler();
      navigateToHome();
    }, 400);

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [status, setTokens, setUser, navigateToHome]);

  // On mount: check if Web3Auth already has an active session (user logged in before).
  // If so, skip the login buttons and complete the bridge immediately.
  // This does NOT open a popup — it reads the existing session only.
  const onMount = useCallback(() => {
    if (!bridgeId) return;

    // This page owns login completion on its route (AuthContext stands down here),
    // so it takes the restored-session callback itself: v11 can restore a live
    // session after init() resolves, which the connected check below would miss.
    web3authGateway.setOnLoginSuccessCallback((userInfo) => {
      void completeAuth(userInfo);
    });

    void (async () => {
      // A redirect return takes a few seconds to restore before it can complete —
      // show progress instead of the login buttons meanwhile.
      if (web3authGateway.isRestoringRedirect) setStatus('completing');

      try {
        await web3authGateway.init();

        if (web3authGateway.connected) {
          setStatus('completing');
          const existingUser = await web3authGateway.getExistingSession();
          if (existingUser) {
            void completeAuth(existingUser);
            return;
          }
        }
      } catch {
        // No existing session — user needs to click a button
      }
      // No session found — undo only our own 'completing', never a login the
      // user started meanwhile, so they can click login.
      setStatus((current) => (current === 'completing' ? 'ready' : current));
    })();
  }, [bridgeId, completeAuth]);

  return {
    state: { status, error, provider, bridgeId },
    actions: { handleGoogleLogin, handleFacebookLogin, retry },
    onMount
  };
}
